import * as React from "react"
import { create } from "zustand"

type MobileHeaderTintState = {
  /**
   * How many mounted screens want the phone header tinted (a counter, so a screen leaving while another arrives
   * never switches the tint off under the newcomer).
   */
  tintRequests: number
  add: () => void
  remove: () => void
}

export const useMobileHeaderTintStore = create<MobileHeaderTintState>((set) => ({
  tintRequests: 0,
  add: () => set((state) => ({ tintRequests: state.tintRequests + 1 })),
  remove: () => set((state) => ({ tintRequests: Math.max(0, state.tintRequests - 1) })),
}))

/**
 * For a screen whose top is a soft green band (home's balance, a member's profile): tints the phone header with
 * the band's top colour while the screen is mounted, so the band runs up under the header with no edge.
 */
export function useMobileHeaderTint(enabled = true) {
  const add = useMobileHeaderTintStore((state) => state.add)
  const remove = useMobileHeaderTintStore((state) => state.remove)
  React.useEffect(() => {
    if (!enabled) return
    add()
    return remove
  }, [add, enabled, remove])
}

/** The band and the tinted header share this exact colour, so where one ends and the other begins cannot show. */
export const MOBILE_HEADER_TINT_CLASS = "bg-primary/12"
/** The tint class while a screen asks for it, else undefined: for surfaces that must match the header, like the pull-to-refresh gap. */
export function useMobileHeaderTintClass() {
  return useMobileHeaderTintStore((state) => state.tintRequests > 0) ? MOBILE_HEADER_TINT_CLASS : undefined
}
/** A hero band: starts at the header's tint and fades into the page. */
export const MOBILE_HERO_BAND_CLASS = "bg-gradient-to-b from-primary/12 to-transparent"
